/**
 * Terms and dictionary lists: the language vocabulary, and what a term is.
 *
 * A list is a name, one language and a set of terms with no definitions. Nothing here reads
 * or writes; the browser composes a list with these rules and the backend validates the
 * result with the same ones, so the two can never disagree about what was typed.
 *
 * Deliberately dependency-free — its one import is the generated language table beside it, which
 * is data — so both runtimes can import it, including a Convex module the schema evaluates.
 */
import { AZURE_TRANSCRIPTION_LANGUAGES } from "./transcription-languages.generated.js";
/** A list that belongs to no one language. Every consumer matches it, whatever it is set to. */
export const KNOWLEDGE_DICTIONARY_ANY_LANGUAGE = "any";
/**
 * The codes a list's language may hold, in the one order every picker shows them: "any"
 * first, then alphabetically by English label.
 *
 * **The vocabulary is exactly what Azure AI Speech transcribes**, generated from the locale list
 * the service publishes (`transcription-languages.generated.ts`, refreshed by
 * `scripts/refresh-transcription-languages.mjs`). A terms list exists so a consumer can
 * *reproduce* a word, and the consumer that reproduces most of them is the transcription lane, so
 * offering a language nothing here can transcribe offers a list nothing will ever read.
 *
 * Languages, not locales. A professional term belongs to a project or a company rather than
 * to a regional variant, so a consumer holding `en-US` matches `en` on the prefix instead of
 * asking for a variant nobody was offered.
 *
 * The order is load-bearing twice: it is the member order of the Convex validator derived
 * from this array, so reordering it changes the emitted validator JSON and therefore the
 * schema fingerprint, and it is the order the language select renders.
 */
export const KNOWLEDGE_DICTIONARY_LANGUAGE_CODES = [
    KNOWLEDGE_DICTIONARY_ANY_LANGUAGE,
    ...AZURE_TRANSCRIPTION_LANGUAGES.map((entry) => entry.code),
];
/**
 * The one place each language is named in English; the backend renders labels too.
 *
 * Derived from the generated list rather than typed beside it — a hand-written label is a second
 * place a language can be spelled, and this vocabulary moves whenever the vendor's does.
 */
const KNOWLEDGE_DICTIONARY_LANGUAGE_LABELS = {
    [KNOWLEDGE_DICTIONARY_ANY_LANGUAGE]: "Any language",
    ...Object.fromEntries(AZURE_TRANSCRIPTION_LANGUAGES.map((entry) => [entry.code, entry.label])),
};
/** The canonical language options: one ordering, one label, every picker. */
export const KNOWLEDGE_DICTIONARY_LANGUAGES = KNOWLEDGE_DICTIONARY_LANGUAGE_CODES.map((code) => ({
    code,
    label: KNOWLEDGE_DICTIONARY_LANGUAGE_LABELS[code] ?? code,
}));
const KNOWLEDGE_DICTIONARY_LANGUAGE_CODE_SET = new Set(KNOWLEDGE_DICTIONARY_LANGUAGE_CODES);
export function isKnowledgeDictionaryLanguage(value) {
    return typeof value === "string" && KNOWLEDGE_DICTIONARY_LANGUAGE_CODE_SET.has(value);
}
/** The label for a stored language; a code from outside the vocabulary names itself. */
export function formatKnowledgeDictionaryLanguage(code) {
    return KNOWLEDGE_DICTIONARY_LANGUAGE_LABELS[code] ?? code;
}
/**
 * How far back a read walks a project's terms lists before giving up: by rows, and by the text
 * those rows carry.
 *
 * Far below the walk over its statements: a list is one document holding up to 500 terms, and a
 * text query has to read every term of every candidate to decide it. The row bound alone is not
 * enough — five hundred terms of a hundred and twenty characters is some sixty thousand characters
 * per list, and four hundred such lists would exhaust a transaction's read budget on their own —
 * so the walk also stops once the lists it has read add up to the character bound. Every reader
 * shares both — the Knowledge screen, the pinned-prompt loader and `knowledge_search` — so the
 * three are looking at the same window of a project's vocabulary.
 */
export const PROJECT_DICTIONARY_SCAN_LIMIT = 400;
export const PROJECT_DICTIONARY_SCAN_MAX_CHARS = 400_000;
/** One list is one Convex document, so both dimensions of it are bounded. */
export const KNOWLEDGE_DICTIONARY_MAX_TERMS = 500;
export const KNOWLEDGE_DICTIONARY_MAX_TERM_LENGTH = 120;
export const KNOWLEDGE_DICTIONARY_MAX_NAME_LENGTH = 120;
/**
 * The terms a list really holds: trimmed, inner whitespace runs collapsed, blanks dropped,
 * and each spelling kept once in the order it was first typed.
 *
 * Case is preserved and compared, so "Vorne" and "vorne" are two terms. Spelling is the whole
 * point of a list — a consumer that has to reproduce a name cannot be handed a version of it
 * nobody wrote.
 */
export function normalizeDictionaryTerms(terms) {
    const normalized = [];
    const seen = new Set();
    for (const term of terms) {
        const cleaned = term.replace(/\s+/g, " ").trim();
        if (!cleaned || seen.has(cleaned))
            continue;
        seen.add(cleaned);
        normalized.push(cleaned);
    }
    return normalized;
}
/** The terms in a pasted or typed block: commas and line breaks both separate one term from the next. */
export function splitDictionaryTermsInput(text) {
    return normalizeDictionaryTerms(text.split(/[\n\r,]+/));
}
/** A list's name, or nothing at all when it was left blank. The name is never a term. */
export function normalizeDictionaryName(name) {
    const trimmed = name?.trim();
    return trimmed ? trimmed : undefined;
}
